import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Pencil, Trash2, FileSpreadsheet, FileText, Search, Paperclip, Eye, Fingerprint, Loader2 } from "lucide-react";
import { api, API, downloadExport, fileViewUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge2, statusColor, validityFromDate } from "@/components/ComplianceBadge";
import { FileDropzone } from "@/components/FileDropzone";
import { SignaturePad } from "@/components/SignaturePad";
import { BatchImport } from "@/components/BatchImport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export function PageHeader({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0B2545] text-white">
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0B2545] sm:text-3xl">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export default function CrudModule({
  title, subtitle, icon, endpoint, exportKey, columns, fields, testid, employees = [], headerExtra,
  catalogEndpoint, employeeFilter = false, fichaPath, signAction = false, rowFilter, rowExtra, rowLocked, noDelete = false,
  hideBatchImport = false, sidebar, onChanged,
}) {
  const [aiReading, setAiReading] = useState(false);
  const { search, sector } = useOutletContext() || {};
  const { canEdit, canManage } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [empFilter, setEmpFilter] = useState("all");
  const [signRow, setSignRow] = useState(null);
  const [signValue, setSignValue] = useState(null);
  const [signSaving, setSignSaving] = useState(false);

  const openSign = (row) => {
    setSignRow(row);
    setSignValue(row.signature || null);
  };

  const doSign = async () => {
    if (!signRow) return;
    setSignSaving(true);
    try {
      await api.put(`/${endpoint}/${signRow.id}`, { ...signRow, signature: signValue });
      toast.success("Assinatura registrada");
      setSignRow(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSignSaving(false);
    }
  };

  useEffect(() => {
    if (!catalogEndpoint) return;
    api.get(`/${catalogEndpoint}`).then(({ data }) => setCatalog(data)).catch(() => {});
  }, [catalogEndpoint]);

  const openFicha = () => {
    if (empFilter === "all" || !fichaPath) return;
    window.open(`${API}/${fichaPath}/${empFilter}/pdf`, "_blank");
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      setRows(data);
    } catch {
      toast.error("Falha ao carregar dados");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [endpoint]); // eslint-disable-line

  const openNew = () => {
    const init = {};
    fields.forEach((f) => {
      init[f.name] = f.default !== undefined ? f.default : f.type === "number" ? 0 : "";
    });
    setForm(init);
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({ ...row });
    setEditing(row);
    setOpen(true);
  };

  const save = async () => {
    for (const f of fields) {
      if (f.required && !form[f.type === "employee" && f.idKey ? f.idKey : f.name]) {
        toast.error(`Preencha o campo: ${f.label}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
      });
      if (editing) {
        await api.put(`/${endpoint}/${editing.id}`, payload);
        toast.success("Registro atualizado");
      } else {
        await api.post(`/${endpoint}`, payload);
        toast.success("Registro criado com sucesso");
      }
      setOpen(false);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${deleteId}`);
      toast.success("Registro removido");
      setDeleteId(null);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(q))
      );
    }
    if (sector && sector !== "Todos") {
      out = out.filter((r) => !r.department || r.department === sector);
    }
    if (employeeFilter && empFilter !== "all") {
      out = out.filter((r) => r.employee_id === empFilter);
    }
    if (rowFilter) out = out.filter(rowFilter);
    return out;
  }, [rows, search, sector, employeeFilter, empFilter, rowFilter]);

  const setF = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const fileField = useMemo(() => fields.find((f) => f.type === "file"), [fields]);
  const titleKey = useMemo(() => {
    const t = fields.find((f) => f.required && (!f.type || f.type === "text"))
      || fields.find((f) => !f.type || f.type === "text") || fields[0];
    return t?.name || "title";
  }, [fields]);

  const renderField = (f) => {
    const val = form[f.name] ?? "";
    switch (f.type) {
      case "textarea":
        return (
          <Textarea value={val} onChange={(e) => setF(f.name, e.target.value)}
            rows={3} data-testid={`${testid}-field-${f.name}`} />
        );
      case "number":
        return (
          <Input type="number" value={val} onChange={(e) => setF(f.name, e.target.value)}
            data-testid={`${testid}-field-${f.name}`} />
        );
      case "date":
        return (
          <Input type="date" value={val || ""} onChange={(e) => setF(f.name, e.target.value)}
            data-testid={`${testid}-field-${f.name}`} />
        );
      case "datetime":
        return (
          <Input type="datetime-local" value={val || ""} onChange={(e) => setF(f.name, e.target.value)}
            data-testid={`${testid}-field-${f.name}`} />
        );
      case "select":
        return (
          <Select value={val || ""} onValueChange={(v) => setF(f.name, v)}>
            <SelectTrigger data-testid={`${testid}-field-${f.name}`}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {f.options.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "employee":
        return (
          <Select
            value={form[f.idKey] || ""}
            onValueChange={(v) => {
              const emp = employees.find((e) => e.id === v);
              setForm((p) => ({ ...p, [f.idKey]: v, [f.nameKey]: emp?.name || "" }));
            }}
          >
            <SelectTrigger data-testid={`${testid}-field-${f.name}`}>
              <SelectValue placeholder="Selecione o colaborador" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name} — {e.department}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "file":
        return (
          <>
            <FileDropzone
              value={form[f.idKey]}
              valueName={form[f.nameKey]}
              onUploaded={async ({ id, name }) => {
                setForm((p) => ({ ...p, [f.idKey]: id, [f.nameKey]: name }));
                if (!f.aiFill || !id) return;
                setAiReading(true);
                try {
                  const { data } = await api.post(f.aiFill, { file_id: id }, { timeout: 120000 });
                  setForm((p) => {
                    const next = { ...p };
                    Object.entries(data).forEach(([k, v]) => { if (v && fields.some((x) => x.name === k)) next[k] = v; });
                    return next;
                  });
                  toast.success("IA leu o documento e preencheu os campos. Confira e clique em Salvar.");
                } catch { toast.error("Não foi possível ler o documento automaticamente. Preencha manualmente."); }
                setAiReading(false);
              }}
              testid={`${testid}-upload-${f.name}`}
            />
            {aiReading && <p className="mt-2 flex items-center gap-2 text-xs font-medium text-emerald-700" data-testid={`${testid}-ai-reading`}><Loader2 className="h-3.5 w-3.5 animate-spin" /> Lendo o documento com IA e atualizando os campos...</p>}
          </>
        );
      case "combo":
        return (
          <>
            <Input list={`${testid}-${f.name}-opts`} value={val} onChange={(e) => setF(f.name, e.target.value)}
              placeholder="Selecione ou digite um novo" data-testid={`${testid}-field-${f.name}`} />
            <datalist id={`${testid}-${f.name}-opts`}>{(f.options || []).map((o) => <option key={o} value={o} />)}</datalist>
          </>
        );
      case "signature":
        return (
          <SignaturePad value={form.signature} onChange={(v) => setF("signature", v)}
            tablet={f.tablet} label={f.label}
            testid={`${testid}-signature`} />
        );
      case "catalog":
        return (
          <>
            <Input list={`${testid}-${f.name}-catalog`} value={val}
              onChange={(e) => {
                const v = e.target.value;
                const item = catalog.find((c) => c.name === v);
                setForm((p) => {
                  const next = { ...p, [f.name]: v };
                  if (item && f.fill) {
                    Object.entries(f.fill).forEach(([field, ck]) => { next[field] = item[ck] ?? ""; });
                  }
                  return next;
                });
              }}
              placeholder="Digite ou selecione do catálogo de EPIs"
              data-testid={`${testid}-field-${f.name}`} />
            <datalist id={`${testid}-${f.name}-catalog`}>
              {catalog.map((c) => (
                <option key={c.id} value={c.name}>{c.ca_number ? `CA ${c.ca_number}` : ""}{c.manufacturer ? ` · ${c.manufacturer}` : ""}</option>
              ))}
            </datalist>
          </>
        );
      default:
        return (
          <Input value={val} onChange={(e) => setF(f.name, e.target.value)}
            data-testid={`${testid}-field-${f.name}`} />
        );
    }
  };

  const renderCell = (col, row) => {
    const raw = row[col.key];
    if (col.render) return col.render(row);
    if (col.badge) {
      const color = col.badge === "validity" ? validityFromDate(raw).color : statusColor(raw);
      const label = col.badge === "validity" ? validityFromDate(raw).label : raw;
      return <Badge2 color={color}>{label || "—"}</Badge2>;
    }
    if (col.file) {
      const id = row[col.idKey];
      return id ? (
        <a href={fileViewUrl(id)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[#134074] hover:underline" data-testid={`${testid}-file-${row.id}`}>
          <Eye className="h-3.5 w-3.5" /> Ver
        </a>
      ) : (
        <span className="text-slate-300"><Paperclip className="h-3.5 w-3.5" /></span>
      );
    }
    return <span className="text-slate-700">{raw ?? "—"}</span>;
  };

  return (
    <div data-testid={`${testid}-page`}>
      <PageHeader icon={icon} title={title} subtitle={subtitle}>
        {headerExtra}
        {employeeFilter && (
          <>
            <Select value={empFilter} onValueChange={setEmpFilter}>
              <SelectTrigger className="h-9 w-[200px] border-[#E2E8F0] text-sm" data-testid={`${testid}-emp-filter`}>
                <SelectValue placeholder="Filtrar colaborador" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="all">Todos os colaboradores</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {empFilter !== "all" && fichaPath && (
              <Button variant="outline" size="sm" onClick={openFicha}
                className="border-[#0B2545]/30 text-[#0B2545] hover:bg-[#0B2545]/5" data-testid={`${testid}-ficha-btn`}>
                <FileText className="mr-1.5 h-4 w-4" /> Ficha (PDF)
              </Button>
            )}
          </>
        )}
        {canEdit && fileField && !hideBatchImport && (
          <BatchImport target={endpoint} idKey={fileField.idKey} nameKey={fileField.nameKey}
            titleKey={titleKey} onDone={load} testid={`${testid}-batch`} aiAso={["medical-records", "ppe-deliveries", "safety-talks", "licenses"].includes(endpoint)} />
        )}
        {exportKey && (
          <>
            <Button variant="outline" size="sm" onClick={() => downloadExport(exportKey, "excel")}
              data-testid={`${testid}-export-excel`} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadExport(exportKey, "pdf")}
              data-testid={`${testid}-export-pdf`} className="border-rose-200 text-rose-700 hover:bg-rose-50">
              <FileText className="mr-1.5 h-4 w-4" /> PDF
            </Button>
          </>
        )}
        {canEdit && (
          <Button onClick={openNew} data-testid={`${testid}-new-btn`} className="bg-[#0B2545] hover:bg-[#134074]">
            <Plus className="mr-1.5 h-4 w-4" /> Novo
          </Button>
        )}
      </PageHeader>

      <div className={sidebar ? "grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]" : ""}>
      {sidebar}
      <div className="min-w-0 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E8F0] bg-slate-50 text-left">
                {columns.map((c) => (
                  <th key={c.key || c.label} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {c.label}
                  </th>
                ))}
                {(canEdit || canManage) && <th className="sticky right-0 bg-slate-50 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.15)]">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-400">Carregando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center">
                  <Search className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                  <p className="text-slate-400">Nenhum registro encontrado</p>
                </td></tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`${testid}-row`}>
                    {columns.map((c) => (
                      <td key={c.key || c.label} className="whitespace-nowrap px-4 py-3">{renderCell(c, row)}</td>
                    ))}
                    {(canEdit || canManage) && (
                      <td className="sticky right-0 whitespace-nowrap bg-white px-4 py-3 text-right shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.15)]" data-testid={`${testid}-actions-${row.id}`}>
                        {canEdit && rowExtra && rowExtra(row, load)}
                        {canEdit && signAction && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openSign(row)} data-testid={`${testid}-sign-${row.id}`} title="Assinar (tablet)">
                            <Fingerprint className={`h-4 w-4 ${row.signature ? "text-emerald-600" : "text-[#134074]"}`} />
                          </Button>
                        )}
                        {canEdit && !(rowLocked && rowLocked(row)) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid={`${testid}-edit-${row.id}`}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                        )}
                        {canManage && !noDelete && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteId(row.id)} data-testid={`${testid}-delete-${row.id}`}>
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="text-[#0B2545]">{editing ? "Editar" : "Novo"} — {title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="mb-1.5 block text-xs font-semibold text-slate-600">
                  {f.label}{f.required && <span className="text-rose-500"> *</span>}
                </Label>
                {renderField(f)}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || aiReading} className="bg-[#0B2545] hover:bg-[#134074]" data-testid={`${testid}-save-btn`}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signRow} onOpenChange={(o) => !o && setSignRow(null)}>
        <DialogContent className="sm:max-w-[560px]" data-testid={`${testid}-sign-dialog`}>
          <DialogHeader>
            <DialogTitle className="text-[#0B2545]">Assinatura do colaborador</DialogTitle>
          </DialogHeader>
          {signRow && (
            <p className="text-sm text-slate-500">
              {signRow.employee_name ? <><b>{signRow.employee_name}</b> — </> : null}
              {signRow.ppe_name || signRow.title || ""} {signRow.ca_number ? `· CA ${signRow.ca_number}` : ""}
            </p>
          )}
          <SignaturePad value={signValue} onChange={setSignValue} tablet
            label="Assinatura do colaborador" testid={`${testid}-sign-pad`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignRow(null)}>Cancelar</Button>
            <Button onClick={doSign} disabled={signSaving} className="bg-[#0B2545] hover:bg-[#134074]" data-testid={`${testid}-sign-save`}>
              {signSaving ? "Salvando..." : "Salvar assinatura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. O registro será removido permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid={`${testid}-confirm-delete`}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Hook to load employees for select fields
export function useEmployees() {
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);
  return employees;
}
